import React, {useState, useEffect, useRef, useMemo} from "react";
import SpeechRecognitionEngine from "./SpeechRecognitionEngine";
import {Header, SearchResultsContent, ConversationHistoryPanel} from "./components";
import {detectBaseUrl} from "./utils/utils";
import getApiConfig from "./config/apiConfig";
import {chatConversation, getLatestConversationSession} from "../common/services";

/**
 * SpeechSearchModal component
 * Main component for the speech search modal
 */
const SpeechSearchModal = ({onClose, handleTranscriptUpdate, currentText, currentHold}) => {
    const [is_data_load, setIsDataLoad] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [baseUrl, setBaseUrl] = useState(detectBaseUrl());
    const [isHistoryVisible, setIsHistoryVisible] = useState(false);
    const [conversationHistory, setConversationHistory] = useState([]);
    const [resultsByQuery, setResultsByQuery] = useState({});
    const [historySelection, setHistorySelection] = useState(null);
    const [headerSelection, setHeaderSelection] = useState(null);
    const [isAwaitingAssistantReply, setIsAwaitingAssistantReply] = useState(false);
    const [activeSearchQuery, setActiveSearchQuery] = useState('');

    const contentRef = useRef(null);
    const headerWrapperRef = useRef(null);
    const historyIdRef = useRef(0);
    const latestFinalizedTextRef = useRef('');
    const latestAssistantRequestIdRef = useRef(0);
    const hasHydratedLatestSessionRef = useRef(false);
    const [historyOverlayTop, setHistoryOverlayTop] = useState(0);
    const apiConfig = getApiConfig();

    const latestAssistantReply = useMemo(() => {
        const latestAssistantMessage = [...conversationHistory]
            .reverse()
            .find((item) => item.role === 'assistant' && typeof item.text === 'string' && item.text.trim());

        return latestAssistantMessage ? latestAssistantMessage.text : '';
    }, [conversationHistory]);

    const latestUserText = useMemo(() => {
        const latestUserMessage = [...conversationHistory]
            .reverse()
            .find((item) => item.role === 'user' && typeof item.text === 'string' && item.text.trim());

        return latestUserMessage ? latestUserMessage.text : '';
    }, [conversationHistory]);

    const liveText = typeof currentText === 'string' ? currentText.trim() : '';
    const isLiveConversation = Boolean(liveText) || currentHold === true;
    const headerConversationText = isLiveConversation
        ? currentText
        : (headerSelection?.userText || latestUserText);
    const headerAssistantReply = isLiveConversation
        ? latestAssistantReply
        : (headerSelection?.assistantReply || latestAssistantReply);

    const pushHistoryItem = (item) => {
        setConversationHistory((prev) => [...prev, {id: `msg_${++historyIdRef.current}`, ...item}]);
    };

    useEffect(() => {
        setBaseUrl(detectBaseUrl());
    }, []);

    useEffect(() => {
        const finalizedText = typeof currentText === 'string' ? currentText.trim() : '';
        const hasLiveConversation = Boolean(finalizedText) || conversationHistory.length > 0;

        if (hasHydratedLatestSessionRef.current || hasLiveConversation) {
            return;
        }

        let mounted = true;
        hasHydratedLatestSessionRef.current = true;

        (async () => {
            const latestData = await getLatestConversationSession({
                merchant: window.__VOICE_SEARCH_CONFIG__?.merchant || window.location.hostname,
                clientKey: apiConfig.clientKey
            });

            if (!mounted) return;

            const latestSession = Array.isArray(latestData?.sessions) ? latestData.sessions[0] : null;
            const turns = Array.isArray(latestSession?.turns) ? latestSession.turns : [];

            if (!turns.length) {
                return;
            }

            const normalizedHistory = turns
                .filter((turn) => turn && (turn.role === 'user' || turn.role === 'assistant'))
                .map((turn, index) => {
                    const normalizedText = typeof turn.text === 'string' ? turn.text.trim() : '';
                    const semanticSentence = typeof turn.semantic_sentence === 'string' ? turn.semantic_sentence.trim() : '';
                    const query = semanticSentence || normalizedText;
                    return {
                        id: `msg_${index + 1}`,
                        role: turn.role,
                        text: normalizedText,
                        timestamp: turn.timestamp || new Date().toISOString(),
                        query,
                        semanticSentence: semanticSentence || null,
                        products: []
                    };
                })
                .filter((item) => item.text);

            if (!normalizedHistory.length || !mounted) {
                return;
            }

            historyIdRef.current = normalizedHistory.length;
            setConversationHistory(normalizedHistory);

            const lastUserTurn = [...normalizedHistory].reverse().find((item) => item.role === 'user');
            if (lastUserTurn?.text) {
                latestFinalizedTextRef.current = lastUserTurn.text;
            }

            const latestAssistantTurn = [...normalizedHistory].reverse().find((item) => item.role === 'assistant' && item.query);
            if (latestAssistantTurn?.query) {
                setActiveSearchQuery(latestAssistantTurn.query);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [apiConfig.clientKey, conversationHistory.length, currentText]);

    useEffect(() => {
        const updateHistoryOffset = () => {
            if (!headerWrapperRef.current) return;
            setHistoryOverlayTop(headerWrapperRef.current.offsetHeight + 8);
        };

        updateHistoryOffset();

        if (typeof window === 'undefined') return;
        window.addEventListener('resize', updateHistoryOffset);

        let observer;
        if (typeof ResizeObserver !== 'undefined' && headerWrapperRef.current) {
            observer = new ResizeObserver(updateHistoryOffset);
            observer.observe(headerWrapperRef.current);
        }

        return () => {
            window.removeEventListener('resize', updateHistoryOffset);
            if (observer) observer.disconnect();
        };
    }, [is_data_load]);

    useEffect(() => {
        const isFinalText = currentHold === false;
        const finalizedText = typeof currentText === 'string' ? currentText.trim() : '';

        if (!isFinalText || !finalizedText) {
            return;
        }
        if (latestFinalizedTextRef.current === finalizedText) {
            return;
        }

        latestFinalizedTextRef.current = finalizedText;
        const requestId = ++latestAssistantRequestIdRef.current;
        setIsAwaitingAssistantReply(true);
        setHeaderSelection(null);
        setActiveSearchQuery('');

        pushHistoryItem({
            role: 'user',
            text: finalizedText,
            timestamp: new Date().toISOString(),
            query: finalizedText,
            products: []
        });

        (async () => {
            try {
                const assistantResponse = await chatConversation({
                    message: finalizedText,
                    merchant: window.__VOICE_SEARCH_CONFIG__?.merchant || window.location.hostname,
                    clientKey: apiConfig.clientKey
                });

                const semanticQuery = typeof assistantResponse?.semantic_sentence === 'string'
                    ? assistantResponse.semantic_sentence.trim()
                    : '';
                const assistantReplyText = typeof assistantResponse?.reply === 'string'
                    ? assistantResponse.reply.trim()
                    : '';
                const queryForSearch = semanticQuery || finalizedText;
                setActiveSearchQuery(queryForSearch);

                const productsForQuery = Array.isArray(resultsByQuery[queryForSearch]) ? resultsByQuery[queryForSearch] : [];

                const isRequestFailed = assistantResponse?.intent === 'request_failed';
                const messageToShow = assistantReplyText || (isRequestFailed ? 'Sorry, I could not process that request right now.' : '');

                if (!messageToShow) {
                    return;
                }

                pushHistoryItem({
                    role: 'assistant',
                    text: messageToShow,
                    timestamp: assistantResponse?.timestamp || new Date().toISOString(),
                    query: queryForSearch,
                    products: productsForQuery,
                    intent: assistantResponse?.intent || null,
                    semanticSentence: semanticQuery || null,
                });
            } finally {
                if (requestId === latestAssistantRequestIdRef.current) {
                    setIsAwaitingAssistantReply(false);
                }
            }
        })();
    }, [currentText, currentHold, apiConfig.clientKey, resultsByQuery]);

    const handleResultsReady = (query, products) => {
        const safeQuery = typeof query === 'string' ? query.trim() : '';
        if (!safeQuery) return;

        setResultsByQuery((prev) => ({...prev, [safeQuery]: Array.isArray(products) ? products : []}));

        setConversationHistory((prev) => prev.map((item) => {
            if (item.role === 'assistant' && item.query === safeQuery) {
                return {
                    ...item,
                    products: Array.isArray(products) ? products : item.products
                };
            }
            return item;
        }));
    };

    const handleAssistantMessageClick = (item) => {
        if (!item) return;
        const restoredQuery = typeof item.query === 'string' ? item.query.trim() : '';
        const restoredProducts = Array.isArray(item.products) ? item.products : [];
        const restoredReply = typeof item.text === 'string' ? item.text.trim() : '';
        if (!restoredQuery && restoredProducts.length === 0) return;

        const itemIndex = conversationHistory.findIndex((entry) => entry.id === item.id);
        const restoredUserText = itemIndex > -1
            ? [...conversationHistory.slice(0, itemIndex + 1)]
                .reverse()
                .find((entry) => entry.role === 'user' && typeof entry.text === 'string' && entry.text.trim())?.text || ''
            : '';

        setHistorySelection({
            query: restoredQuery,
            products: restoredProducts
        });
        setHeaderSelection({
            userText: restoredUserText,
            assistantReply: restoredReply
        });
        setIsAwaitingAssistantReply(false);

        if (restoredQuery) {
            setActiveSearchQuery(restoredQuery);
        }
        setHasScrolled(false);
    };

    return (
        <div className="fixed inset-0 flex flex-col pt-[10vh] z-[999]">
            <div className="bg-black bg-opacity-50 absolute inset-0 -z-10"></div>
            <div
                className="flex-grow flex flex-col rounded-tl-2xl rounded-tr-2xl shadow-2xl w-11/12 mx-auto overflow-hidden relative">
                <div ref={headerWrapperRef}>
                    <Header
                        baseUrl={baseUrl}
                        onClose={onClose}
                        currentText={headerConversationText}
                        currentHold={currentHold}
                        is_data_load={is_data_load}
                        apiConfig={apiConfig}
                        isHistoryVisible={isHistoryVisible}
                        onToggleHistory={() => conversationHistory.length && setIsHistoryVisible((prev) => !prev)}
                        assistantReply={headerAssistantReply}
                        isLiveConversation={isLiveConversation}
                        isAwaitingAssistantReply={isAwaitingAssistantReply}
                    />
                </div>

                <SearchResultsContent
                    contentRef={contentRef}
                    hasScrolled={hasScrolled}
                    setHasScrolled={setHasScrolled}
                    loading={loading}
                    is_data_load={is_data_load}
                    setLoading={setLoading}
                    setIsDataLoad={setIsDataLoad}
                    searchQuery={activeSearchQuery}
                    currentText={currentText}
                    currentHold={currentHold}
                    apiConfig={apiConfig}
                    onResultsReady={handleResultsReady}
                    historySelection={historySelection}
                    isHistoryVisible={isHistoryVisible}
                    conversationHistory={conversationHistory}
                    onAssistantMessageClick={handleAssistantMessageClick}
                    onToggleHistory={() => conversationHistory.length &&  setIsHistoryVisible((prev) => !prev)}

                />

                <SpeechRecognitionEngine
                    onTranscriptUpdate={handleTranscriptUpdate}
                    isItModal={!is_data_load}
                    loading={loading}
                    isModalOpen={true}
                />
            </div>
        </div>
    );
};

export default SpeechSearchModal;
